import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../AuthContext";
import { useI18n } from "../i18n";
import { SecondaryHeader, Footer } from "../components/Frumy";
import { ShieldCheck, MagnifyingGlass, ChartLineUp, ArrowRight, UploadSimple, Cpu, ChartPieSlice } from "@phosphor-icons/react";

const diffIcons = [ShieldCheck, MagnifyingGlass, ChartLineUp];
const stepIcons = [UploadSimple, Cpu, ChartPieSlice];

export default function About() {
  const { t } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const diffPoints = t("aboutDiffPoints");
  const steps = t("aboutSteps");

  return (
    <div className="min-h-screen">
      <SecondaryHeader />

      {/* Hero */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center pt-8 lg:pt-14 pb-14">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .5 }}>
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-[var(--frumy-green)]">Frumy</span>
          <h1 data-testid="about-title" className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mt-3 leading-[1.05] text-[var(--heading)]">
            {t("aboutTitle")}
          </h1>
          <p className="text-base leading-relaxed text-[var(--text-muted)] mt-5 max-w-md">{t("aboutSubtitle")}</p>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: .96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: .5, delay: .1 }}
          className="justify-self-center lg:justify-self-end w-full max-w-md">
          <HowItWorksSVG />
        </motion.div>
      </div>

      {/* Why */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <h2 className="font-display text-2xl font-extrabold text-[var(--heading)]">{t("aboutWhyTitle")}</h2>
        <p className="text-base leading-relaxed text-[var(--text-muted)] mt-4">{t("aboutWhyBody")}</p>
      </section>

      {/* What makes it different */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <h2 className="font-display text-2xl font-extrabold text-[var(--heading)] mb-6">{t("aboutDiffTitle")}</h2>
        <div className="grid md:grid-cols-3 gap-5">
          {diffPoints.map((d, i) => {
            const Icon = diffIcons[i] || ShieldCheck;
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: .4, delay: i * .08 }} className="frumy-card p-6" data-testid={`about-diff-${i}`}>
                <span className="grid place-items-center h-11 w-11 rounded-xl brand-gradient mb-4">
                  <Icon weight="duotone" size={22} aria-hidden />
                </span>
                <h3 className="font-bold text-[var(--heading)]">{d.h}</h3>
                <p className="text-sm leading-relaxed text-[var(--text-muted)] mt-2">{d.p}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Evolving product */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="frumy-card p-7 sm:p-9 relative overflow-hidden">
          <div className="relative z-10 max-w-2xl">
            <h2 className="font-display text-2xl font-extrabold text-[var(--heading)]">{t("aboutEvolveTitle")}</h2>
            <p className="text-base leading-relaxed text-[var(--text-muted)] mt-4">{t("aboutEvolveBody")}</p>
          </div>
          <div className="absolute -right-10 -bottom-10 h-52 w-52 rounded-full brand-gradient opacity-10 blur-2xl" aria-hidden />
        </div>
      </section>

      {/* 3 steps */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <h2 className="font-display text-2xl font-extrabold text-[var(--heading)] mb-6">{t("aboutStepsTitle")}</h2>
        <div className="grid md:grid-cols-3 gap-5">
          {steps.map((s, i) => {
            const Icon = stepIcons[i] || UploadSimple;
            return (
              <div key={i} className="frumy-card p-6 flex items-start gap-4" data-testid={`about-step-${i}`}>
                <span className="num-hero text-3xl text-gradient shrink-0">{i + 1}</span>
                <div>
                  <span className="grid place-items-center h-9 w-9 rounded-lg bg-[var(--frumy-muted)] text-[var(--frumy-green)] mb-3">
                    <Icon weight="duotone" size={18} aria-hidden />
                  </span>
                  <p className="text-sm font-semibold leading-relaxed text-[var(--heading)]">{s}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-10 flex justify-center">
          <button data-testid="about-cta" onClick={() => nav(user ? "/upload" : "/")}
            className="pill-btn cta-metal inline-flex items-center gap-2 text-white font-bold px-7 py-3.5">
            {t("aboutCta")}<ArrowRight weight="bold" size={18} aria-hidden />
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function HowItWorksSVG() {
  return (
    <svg viewBox="0 0 420 340" className="w-full h-auto brand-ring rounded-2xl" role="img" aria-label="Frumy analysis illustration">
      <defs>
        <linearGradient id="fg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#0B6E8C" />
          <stop offset="55%" stopColor="#0E8F8A" />
          <stop offset="100%" stopColor="#10B981" />
        </linearGradient>
        <linearGradient id="bar" x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stopColor="#10B981" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#10B981" />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="420" height="340" rx="18" fill="var(--surface)" />
      <rect x="0" y="0" width="420" height="64" rx="18" fill="url(#fg)" />
      <rect x="0" y="40" width="420" height="24" fill="url(#fg)" />
      <circle cx="30" cy="32" r="8" fill="#fff" opacity="0.9" />
      <rect x="48" y="26" width="120" height="12" rx="6" fill="#fff" opacity="0.85" />
      <rect x="330" y="24" width="60" height="16" rx="8" fill="#fff" opacity="0.25" />

      {/* transaction rows */}
      {[0, 1, 2].map((i) => (
        <g key={i} transform={`translate(24 ${92 + i * 40})`}>
          <rect x="0" y="0" width="200" height="26" rx="8" fill="var(--frumy-muted)" />
          <circle cx="16" cy="13" r="7" fill="url(#fg)" opacity="0.9" />
          <rect x="30" y="8" width={90 - i * 14} height="9" rx="4.5" fill="var(--text-muted)" opacity="0.5" />
          <rect x="150" y="8" width="40" height="9" rx="4.5" fill="url(#fg)" opacity="0.8" />
        </g>
      ))}

      {/* mini bar chart card */}
      <g transform="translate(244 84)">
        <rect x="0" y="0" width="152" height="132" rx="12" fill="var(--frumy-muted)" />
        <rect x="16" y="16" width="70" height="9" rx="4.5" fill="var(--text-muted)" opacity="0.5" />
        {[46, 78, 60, 96, 72].map((h, i) => (
          <rect key={i} x={16 + i * 26} y={116 - h} width="16" height={h} rx="5" fill="url(#bar)" />
        ))}
      </g>

      {/* donut */}
      <g transform="translate(78 250)">
        <circle cx="34" cy="34" r="30" fill="none" stroke="var(--frumy-muted)" strokeWidth="12" />
        <circle cx="34" cy="34" r="30" fill="none" stroke="url(#fg)" strokeWidth="12"
          strokeDasharray="120 188" strokeLinecap="round" transform="rotate(-90 34 34)" />
      </g>
      <rect x="150" y="256" width="120" height="12" rx="6" fill="var(--text-muted)" opacity="0.4" />
      <rect x="150" y="278" width="90" height="12" rx="6" fill="url(#fg)" opacity="0.85" />
      <rect x="150" y="300" width="150" height="10" rx="5" fill="var(--text-muted)" opacity="0.3" />
    </svg>
  );
}
